import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Sparkles, ArrowLeft, Globe } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    email_login_enabled: true,
    phone_login_enabled: false,
    google_login_enabled: false,
    google_client_id: ""
  });
  const [showAdminEmail, setShowAdminEmail] = useState(false);
  const { language, setLanguage, t } = useLanguage();

  useEffect(() => {
    api.get("/settings/public")
      .then(({ data }) => setSettings(data))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      // Check if user is admin
      const { data } = await api.get("/auth/me");
      if (data.role === "admin") navigate("/admin");
      else navigate("/app");
    } catch (e) {
      if (e.response?.status === 403) {
        // Pending verification
        navigate("/verify-otp", { state: { email } });
      } else {
        setErr(formatError(e));
      }
    }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src="https://images.pexels.com/photos/16846853/pexels-photo-16846853.jpeg" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/80 via-primary/40 to-transparent" />
        <div className="relative h-full p-12 flex flex-col justify-between text-white">
          <Link to="/" className="inline-flex items-center gap-2"><Sparkles className="w-5 h-5 text-secondary"/>
            <span className="font-heading text-2xl">Hamarsathi</span>
          </Link>
          <div>
            <h2 className="font-heading text-5xl leading-tight">
              {language === "hi" ? <>आपका स्वागत है।<br/>आपकी कहानी यहाँ शुरू होती है।</> : <>Welcome back.<br/>Your story continues here.</>}
            </h2>
            <p className="mt-4 text-white/85 max-w-md">
              {language === "hi" ? "पूरे झारखंड से नए रिश्तों को देखने के लिए साइन इन करें।" : "Sign in to view new matches from across Jharkhand."}
            </p>
          </div>
          <div className="text-xs uppercase tracking-[0.2em] text-white/70">
            {language === "hi" ? "झारखंड के लिए निर्मित" : "Made for Jharkhand"}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 relative">
        <div className="absolute inset-0 mandala-bg pointer-events-none" />
        <div className="relative w-full max-w-md">
          <div className="flex justify-between items-center mb-6">
            <Link to="/" className="inline-flex items-center text-sm text-muted-foreground hover:text-primary" data-testid="login-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1"/>{language === "hi" ? "पीछे" : "Back"}
            </Link>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLanguage(language === "en" ? "hi" : "en")}
              className="rounded-full flex items-center gap-1.5 px-3 hover:bg-muted"
              data-testid="lang-switch-btn"
            >
              <Globe className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs font-semibold uppercase">{language === "en" ? "हिं" : "EN"}</span>
            </Button>
          </div>
          <h1 className="font-heading text-4xl text-primary">{t("auth.loginTitle")}</h1>
          <p className="text-muted-foreground mt-2">{t("auth.loginSubtitle")}</p>

          {(settings.email_login_enabled || showAdminEmail) ? (
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-semibold mb-1 text-foreground">
                  {language === "hi" ? "मोबाइल नंबर या ईमेल आईडी *" : "Mobile Number or Email Address *"}
                </label>
                <Input
                  id="email"
                  type="text"
                  required
                  placeholder={language === "hi" ? "उदा. 9835760000 या name@gmail.com" : "e.g. 9835760000 or name@gmail.com"}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email"
                  className="mt-1.5"
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-semibold mb-1 text-foreground">{t("auth.password")}</label>
                <div className="mt-1.5">
                  <PasswordInput 
                    id="password" 
                    required 
                    value={password} 
                    onChange={(e) => setPassword(e.target.value)} 
                    data-testid="login-password" 
                  />
                </div>
              </div>
              <div className="flex justify-end -mt-1">
                <Link to="/forgot-password" className="text-sm text-primary hover:underline" data-testid="login-forgot-link">
                  {language === "hi" ? "पासवर्ड भूल गए?" : "Forgot password?"}
                </Link>
              </div>
              {err && <p className="text-sm text-destructive" data-testid="login-error">{err}</p>}
              <Button type="submit" disabled={loading} className="w-full rounded-full h-11 btn-press" data-testid="login-submit-btn">
                {loading ? (language === "hi" ? "लॉग इन हो रहा है..." : "Signing in...") : t("nav.login")}
              </Button>
            </form>
          ) : (
            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 text-amber-900 rounded-xl text-sm space-y-2">
              <p className="font-semibold">{language === "hi" ? "सदस्यों के लिए ईमेल लॉगिन अक्षम है।" : "Email login is disabled for members."}</p>
              <p className="text-amber-800/90 text-xs">
                {language === "hi" ? "कृपया नीचे दिए गए अपने फोन नंबर या गूगल खाते का उपयोग करके साइन इन करें।" : "Please sign in using your phone number or Google account below."}
              </p>
              <button 
                type="button" 
                onClick={() => setShowAdminEmail(true)} 
                className="text-xs text-[#6B1A24] font-medium hover:underline block pt-1 text-left"
                data-testid="admin-signin-toggle-btn"
              >
                {language === "hi" ? "क्या आप एडमिन हैं? यहाँ लॉगिन करें" : "Are you an administrator? Sign in here"}
              </button>
            </div>
          )}

          {(settings.phone_login_enabled || settings.google_login_enabled) && (
            <>
              <div className="relative my-5">
                <div className="ornament-divider" />
              </div>
              <div className="space-y-3">
                {settings.phone_login_enabled && (
                  <Button asChild variant="outline" className="w-full rounded-full h-11 justify-center" data-testid="login-phone-btn">
                    <Link to="/login/phone">{t("auth.phoneLoginBtn")}</Link>
                  </Button>
                )}
                {settings.google_login_enabled && (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full rounded-full h-11 flex items-center justify-center gap-2"
                    onClick={() => {
                      const googleClientId = settings.google_client_id || "1024963400258-ka7ghj9i9hcdobbtrlmhsd87r0biqf71.apps.googleusercontent.com";
                      const redirectUri = encodeURIComponent("https://hamarsathi.com/user/social-login/callback/google");
                      window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${googleClientId}&redirect_uri=${redirectUri}&response_type=code&scope=openid%20email%20profile`;
                    }}
                  >
                    <svg viewBox="0 0 24 24" className="w-5 h-5"><path fill="#EA4335" d="M12 5.04c1.7 0 3.2.6 4.4 1.8l3.3-3.3C17.7 1.6 15 1 12 1 7.2 1 3.2 3.8 1.4 8l3.9 3c1-3 3.8-6 6.7-6z"/><path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.6h6.5c-.3 1.5-1.1 2.8-2.4 3.7l3.7 2.9c2.2-2 3.7-5 3.7-9z"/><path fill="#FBBC05" d="M5.3 14.8c-.3-.8-.4-1.8-.4-2.8s.1-2 .4-2.8L1.4 6.2C.5 8 .0 10 .0 12c0 2 .5 4 1.4 5.8l3.9-3z"/><path fill="#34A853" d="M12 23c3.2 0 6-1.1 8-3l-3.7-2.9c-1.1.7-2.5 1.2-4.3 1.2-3.4 0-6.2-2.3-7.2-5.4l-3.9 3C3.2 20.2 7.2 23 12 23z"/></svg>
                    {language === "hi" ? "गूगल के साथ जारी रखें" : "Continue with Google"}
                  </Button>
                )}
              </div>
            </>
          )}

          <p className="mt-6 text-sm text-muted-foreground text-center">
            {language === "hi" ? "नए हैं? " : "New here? "} 
            <Link to="/register" className="text-primary font-medium hover:underline" data-testid="login-to-register">
              {t("nav.register")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
